// Sends the page's errors to the server (POST /v1/client-errors), which stores them per kind
// (backend/nowish/client_errors.py) for the hub's watchdog: uncaught errors, rejected promises nobody handled, and a part of the
// page that broke (ErrorBoundary). At most MAX reports per page load, each one once; reporting never throws or
// reports its own failures. The public site may post these too (its server lock lets this one write through).
const MAX = 10;
const sent = new Set<string>();

/** The build: the main script's content-hashed file name (Vite's index-<hash>.js). */
function build() {
  const src = [...document.scripts].map((s) => s.src).find((s) => /\/assets\/index-[\w-]+\.js$/.test(s));
  return src ? src.match(/index-([\w-]+)\.js$/)![1]! : null;
}

/** kind: "uncaught" | "rejection" | "part"; what: e.g. "Nowish: the filters broke". */
export function reportError(kind: string, err: unknown, what?: string) {
  try {
    const text = err instanceof Error ? `${err.name}: ${err.message}` : String((err as { message?: unknown })?.message ?? err);
    const message = (what ? `${what}: ${text}` : text).slice(0, 2000);
    if (sent.size >= MAX || sent.has(kind + message)) return;
    sent.add(kind + message);
    const stack = err instanceof Error && err.stack ? err.stack.slice(0, 8000) : null;
    void fetch("/v1/client-errors", {
      method: "POST", keepalive: true, headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ platform: "web", kind, message, stack, url: location.href.slice(0, 2000), build: build() }),
    }).catch(() => {});
  } catch { /* reporting never breaks anything */ }
}

/** Firefox raises this as an error event (no error object, no stack) whenever a resize observer's work caused another
 *  resize in the same frame, though nothing is broken. The only message left out of the reports. */
export const RESIZE_LOOP_NOTICE = "ResizeObserver loop completed with undelivered notifications.";

export function startReporting() {
  addEventListener("error", (ev) => {
    if (!ev.error && ev.message === RESIZE_LOOP_NOTICE) return;
    reportError("uncaught", ev.error ?? ev.message);
  });
  addEventListener("unhandledrejection", (ev) => reportError("rejection", ev.reason));
}
